// src/hooks/useFetchGlobalTemplates.js

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

/**
 * Infinite‑scroll hook for *global (app‑wide)* templates
 * ──────────────────────────────────────────────────────────────
 *   • isTemplate   == true
 *   • templateScope== "global"
 *
 * Optimistic refresh support:
 *   `refreshKey` (2nd arg, default 0) – bump it in the parent component
 *   after a successful publish to force a state reset + fresh query.
 *
 * usage:
 *   const {
 *     globalTemplates,
 *     isLoading,
 *     hasMore,
 *     fetchNextBatch
 *   } = useFetchGlobalTemplates(20, refreshTick);
 */
export function useFetchGlobalTemplates(pageSize = 20, refreshKey = 0) {
  // ─────────────────────────────────────────────────────────
  //  CONTEXT
  // ─────────────────────────────────────────────────────────
  const { currentUser } = useAuth();

  // ─────────────────────────────────────────────────────────
  //  STATE
  // ─────────────────────────────────────────────────────────
  const [globalTemplates, setGlobalTemplates] = useState([]); // loaded docs
  const [lastDoc,         setLastDoc]         = useState(null); // pagination cursor
  const [isLoading,       setIsLoading]       = useState(false);
  const [hasMore,         setHasMore]         = useState(true);

  // Track prior values
  const prevUserIdRef = useRef(null);
  const prevKeyRef    = useRef(refreshKey);

  // ─────────────────────────────────────────────────────────
  //  FETCH NEXT BATCH (callback => reuse)
  // ─────────────────────────────────────────────────────────
  const fetchNextBatch = useCallback(async () => {
//    console.log('[useFetchGlobalTemplates] fetchNextBatch called');

    if (isLoading) { console.log('[useFetchGlobalTemplates] Already loading'); return; }
    if (!hasMore)   { console.log('[useFetchGlobalTemplates] No more to load'); return; }
    if (!currentUser?.uid) {
//      console.log('[useFetchGlobalTemplates] Missing user, aborting fetch');
      return;
    }

    setIsLoading(true);
//    console.log('[useFetchGlobalTemplates] Setting isLoading = true');

    try {
      // Build Firestore query
      let baseQuery = query(
        collection(db, 'forms'),
        where('isTemplate', '==', true),
        where('templateScope', '==', 'global'),
        where('status', 'in', ['draft', 'published']),
        orderBy('publishedAt', 'desc'),
        limit(pageSize)
      );

      if (lastDoc) {
//        console.log('[useFetchGlobalTemplates] startAfter pagination doc:', lastDoc.id);
        baseQuery = query(baseQuery, startAfter(lastDoc));
      }

      const snapshot = await getDocs(baseQuery);
//      console.log('[useFetchGlobalTemplates] Fetched', snapshot.size, 'docs');

      if (snapshot.empty) {
//        console.log('[useFetchGlobalTemplates] No more documents found');
        setHasMore(false);
        return;
      }

      const newTemplates = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
//      console.log('[useFetchGlobalTemplates] New templates:', newTemplates);

      if (newTemplates.length < pageSize) setHasMore(false);

      setGlobalTemplates((prev) => {
//        console.log('[useFetchGlobalTemplates] Appending to state. Prev length:', prev.length);
        return [...prev, ...newTemplates];
      });

      setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
//      console.log('[useFetchGlobalTemplates] Updated lastDoc:', snapshot.docs[snapshot.docs.length - 1].id);
    } catch (err) {
      console.error('[useFetchGlobalTemplates] ❌ Error:', err);
      setHasMore(false);
    } finally {
      setIsLoading(false);
//      console.log('[useFetchGlobalTemplates] Setting isLoading = false');
    }
  }, [isLoading, hasMore, lastDoc, currentUser?.uid, pageSize]);

  // ─────────────────────────────────────────────────────────
  //  RESET + FIRST LOAD on user change OR refresh tick
  // ─────────────────────────────────────────────────────────
  useEffect(() => {
//    console.log('[useFetchGlobalTemplates] useEffect triggered');
//    console.log('  currentUser.uid:', currentUser?.uid);
//    console.log('  refreshKey     :', refreshKey);

    if (!currentUser?.uid) return;

    const userChanged      = prevUserIdRef.current !== currentUser.uid;
    const refreshKeyBumped = prevKeyRef.current    !== refreshKey;

    if (userChanged || refreshKeyBumped) {
//      console.log('[useFetchGlobalTemplates] Resetting state (user change or refresh key bump)');
      setGlobalTemplates([]);
      setLastDoc(null);
      setHasMore(true);
      prevUserIdRef.current = currentUser.uid;
      prevKeyRef.current    = refreshKey;
      fetchNextBatch(); // initial load
    }
    // eslint‑disable‑next‑line react-hooks/exhaustive-deps
  }, [currentUser?.uid, refreshKey, fetchNextBatch]);

  // ─────────────────────────────────────────────────────────
  //  PUBLIC API
  // ─────────────────────────────────────────────────────────
  return {
    globalTemplates,
    isLoading,
    hasMore,
    fetchNextBatch
  };
}
